import { Button } from "@hellocarbo/lasagne";
import { Download } from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";

import { DisplayTypes } from "@/config";
import { UserContext } from "@/contexts";
import { DataPoint } from "@/types";

type TableExportProps = {
  type: DisplayTypes;
  totals: Array<Array<DataPoint>> | null;
  headers: Array<string>;
  rows: Array<Array<DataPoint>>;
  startYear?: number;
  endYear?: number;
};

function ComparisonTableExport({
  type,
  totals,
  headers,
  rows,
  startYear,
  endYear,
}: TableExportProps) {
  const { t } = useTranslation(["common", "reviewCategories", "dashboard"]);

  const transposeAndFormatRows = (
    matrix: Array<Array<DataPoint>>
  ): Array<Array<number>> => {
    // transpose the rows matrix and only return the formatted carbon value
    return matrix[0].map((value, index) =>
      matrix.map((row) => row[index].carbon)
    );
  };

  // Transpose the data matrix and format the values
  const data = transposeAndFormatRows(rows);

  let totalData: Array<number> | null = null;
  let firstColumn = rows[0].map((item) => [t(`reviewCategories:${item.key}`)]);

  if (totals) {
    // Transpose the total data matrix
    totalData = totals.flat().map((item) => item.carbon);
    firstColumn = [
      [t("reviewCategories:total")],
      ...rows[0].map((item) => [t(`reviewCategories:${item.key}`)]),
    ];
  }
  const { user } = use(UserContext);

  const getFileExportName = (): string => {
    const periodStartYear = startYear ? `-${startYear}` : "";
    const periodEndYear = endYear ? `-${endYear}` : "";
    const filter = t(`dashboard:comparison.export.type.${type}`);
    const company = user?.company.name ? `-${user?.company.name}` : "";

    return `export${company}${periodStartYear}${periodEndYear}-${filter}.xls`;
  };

  const exportComparisonTableData = () => {
    if (data && firstColumn) {
      import("xlsx").then(({ utils, writeFile }) => {
        // Create an empty worksheet
        const ws = utils.aoa_to_sheet([[]]);

        // Add the headers
        utils.sheet_add_aoa(
          ws,
          [
            [
              "",
              ...headers.map(
                (header) => `${header} (${t("common:unit.tCO2e")})`
              ),
            ],
          ],
          { origin: "A1" }
        );

        // Add the first column
        utils.sheet_add_aoa(ws, firstColumn, { origin: { r: 1, c: 0 } });

        if (totalData) {
          // Add the total row
          utils.sheet_add_aoa(ws, [totalData], { origin: { r: 1, c: 1 } });
        }
        // Add the data
        utils.sheet_add_aoa(ws, data, {
          origin: { r: totalData ? 2 : 1, c: 1 },
        });

        // Create a workbook and write in the file
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws);

        writeFile(wb, getFileExportName(), { bookType: "xls" });
      });
    }
  };

  return (
    <div className="text-right">
      <Button
        className="mt-2xs group"
        onClick={exportComparisonTableData}
        shape="soft"
        intent="secondary"
        size="small"
      >
        <Download className="size-sm" />
        {t("common:export")}
      </Button>
    </div>
  );
}

export default ComparisonTableExport;
